import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useParams } from "react-router-dom";
import {
  Badge,
  Button,
  Card,
  CardHeader,
  ErrorState,
  PageHeader,
  PhotoTile,
  Skeleton,
} from "@/components/ui";
import { useApi } from "@/lib/api";
import { ago, decimal, personLabel } from "@/lib/format";

/**
 * One review, with everything a moderation decision needs on one screen:
 * the text, the photos, who wrote it, and every report filed against it.
 *
 * Taking it down is a soft delete. The row survives, the score recomputes
 * without it, and restoring puts it back exactly as it was — which is the
 * right trade for an action that is sometimes a mistake.
 */
export function ReviewDetailScreen() {
  const { reviewId = "" } = useParams();
  const api = useApi();
  const queryClient = useQueryClient();

  const review = useQuery({
    queryKey: ["admin", "review", reviewId],
    queryFn: async () => {
      const { data, error } = await api.GET("/admin/reviews/{reviewId}", {
        params: { path: { reviewId } },
      });
      if (error) throw error;
      return data;
    },
  });

  const invalidate = () => {
    void queryClient.invalidateQueries({
      queryKey: ["admin", "review", reviewId],
    });
    void queryClient.invalidateQueries({ queryKey: ["admin", "reviews"] });
  };

  const takeDown = useMutation({
    mutationFn: async () => {
      const { error } = await api.DELETE("/admin/reviews/{reviewId}", {
        params: { path: { reviewId } },
        body: {},
      });
      if (error) throw error;
    },
    onSuccess: invalidate,
  });

  const restore = useMutation({
    mutationFn: async () => {
      const { error } = await api.POST("/admin/reviews/{reviewId}/restore", {
        params: { path: { reviewId } },
      });
      if (error) throw error;
    },
    onSuccess: invalidate,
  });

  if (review.isError) {
    return (
      <div className="flex flex-col gap-4">
        <PageHeader eyebrow="Corpus" title="Review" />
        <ErrorState onRetry={() => void review.refetch()} />
      </div>
    );
  }

  const d = review.data;
  const busy = takeDown.isPending || restore.isPending;

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        eyebrow="Corpus"
        title={d?.review.cafe?.name ?? "Review"}
        description={
          d
            ? `${personLabel(d.review.user)} · ${ago(d.review.createdAt)}`
            : undefined
        }
        actions={
          d ? (
            d.review.deletedAt ? (
              <Button
                variant="primary"
                disabled={busy}
                onClick={() => restore.mutate()}
              >
                Restore
              </Button>
            ) : (
              <Button
                variant="danger"
                disabled={busy}
                onClick={() => takeDown.mutate()}
              >
                Take down
              </Button>
            )
          ) : null
        }
      />

      {!d ? (
        <Skeleton className="h-72" />
      ) : (
        <>
          {d.review.deletedAt ? (
            <p className="rounded-panel border border-border bg-surface-2 px-3 py-2 text-[12px] text-muted">
              Taken down {ago(d.review.deletedAt)}. Hidden everywhere and
              excluded from the cafe score; restoring puts it back untouched.
            </p>
          ) : null}

          <section className="grid gap-3 lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
            <Card>
              <CardHeader
                title="What they wrote"
                action={
                  <span className="flex gap-1.5">
                    {d.review.visibility !== "public" ? (
                      <Badge tone="neutral">{d.review.visibility}</Badge>
                    ) : null}
                    {d.review.openReportCount > 0 ? (
                      <Badge tone="danger">
                        {d.review.openReportCount} open report
                        {d.review.openReportCount === 1 ? "" : "s"}
                      </Badge>
                    ) : null}
                  </span>
                }
              />
              <p className="whitespace-pre-wrap text-[13px] leading-relaxed text-ink-2">
                {d.review.note ?? (
                  <span className="text-muted-2">
                    No note — a rating and nothing else.
                  </span>
                )}
              </p>
              {d.photos.length > 0 ? (
                <div className="mt-4 flex flex-wrap gap-2">
                  {d.photos.map((photo) => (
                    <PhotoTile key={photo.id} photo={photo} />
                  ))}
                </div>
              ) : null}
            </Card>

            <Card>
              <CardHeader title="The drink" />
              <dl className="flex flex-col gap-2">
                <Row
                  label="Matcha"
                  value={
                    <span className="font-mono text-[15px] text-ink">
                      {decimal(d.review.rating)}
                    </span>
                  }
                />
                <Row
                  label="Room"
                  value={
                    <span className="font-mono text-[15px] text-ink">
                      {decimal(d.review.roomRating)}
                    </span>
                  }
                />
                <Row label="Drink" value={d.review.drinkType ?? "—"} />
                <Row label="Milk" value={d.review.milk ?? "—"} />
                <Row label="Sweetened" value={d.sweetened ?? "—"} />
                <Row label="Brand" value={d.brand ?? "—"} />
                <Row
                  label="Cafe"
                  value={
                    d.review.cafe ? (
                      <Link
                        to={`/cafes/${d.review.cafe.id}`}
                        className="text-accent hover:underline"
                      >
                        {d.review.cafe.name}
                      </Link>
                    ) : (
                      "—"
                    )
                  }
                />
                <Row
                  label="Person"
                  value={
                    d.review.user ? (
                      <Link
                        to={`/users/${d.review.user.id}`}
                        className="text-accent hover:underline"
                      >
                        {personLabel(d.review.user)}
                      </Link>
                    ) : (
                      "deleted account"
                    )
                  }
                />
              </dl>
            </Card>
          </section>

          {d.reports.length > 0 ? (
            <Card>
              <CardHeader
                title="Reports"
                subtitle="Everything filed against this review, resolved or not."
              />
              <ul className="flex flex-col gap-2">
                {d.reports.map((report) => (
                  <li
                    key={report.id}
                    className="flex flex-wrap items-center gap-2 border-b border-border-2 pb-2 last:border-b-0 last:pb-0"
                  >
                    <Badge tone={report.resolvedAt ? "neutral" : "danger"}>
                      {report.reason.replace(/_/g, " ")}
                    </Badge>
                    <span className="text-[12px] text-ink-2">
                      {report.note ?? "no note"}
                    </span>
                    <span className="text-micro text-muted">
                      {personLabel(report.reporter)} · {ago(report.createdAt)}
                      {report.resolvedAt
                        ? ` · resolved ${ago(report.resolvedAt)}`
                        : ""}
                    </span>
                  </li>
                ))}
              </ul>
            </Card>
          ) : null}

          {d.comments.length > 0 ? (
            <Card>
              <CardHeader title="Comments" />
              <ul className="flex flex-col gap-2">
                {d.comments.map((comment) => (
                  <li
                    key={comment.id}
                    className="border-b border-border-2 pb-2 last:border-b-0 last:pb-0"
                  >
                    <p className="text-[12px] text-ink-2">
                      {comment.deletedAt ? (
                        <span className="italic text-muted-2">deleted</span>
                      ) : (
                        comment.body
                      )}
                    </p>
                    <p className="text-micro text-muted">
                      {personLabel(comment.user)} · {ago(comment.createdAt)}
                    </p>
                  </li>
                ))}
              </ul>
            </Card>
          ) : null}
        </>
      )}
    </div>
  );
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-baseline gap-3">
      <dt className="w-[80px] shrink-0 text-micro text-muted">{label}</dt>
      <dd className="min-w-0 text-[12px] text-ink-2">{value}</dd>
    </div>
  );
}
